import * as React from 'react'
import { Button } from '@/components/button/Button'
import { InlineMessage, type InlineMessageProps } from '@/components/inline-message/InlineMessage'
import { ModalFooterWithActionsType } from './constants'

type Action = {
  /** Leading icon for the action. */
  icon?: React.ReactNode
  /** Trailing icon for the action. */
  trailingIcon?: React.ReactNode
  /** Label for the action. */
  label: string
  /** Function to call when the action is clicked. */
  onClick: () => void
  /** Disable the control (e.g. when the active modal tab does not apply). */
  disabled?: boolean
}

type PrimaryAction = Action & {
  /** Loading state for the primary action button. */
  loading?: boolean | 'loading' | 'success' | 'error'
}

type TertiaryAction = Action & {
  /** The color of the tertiary action button. Defaults to `neutral` for `outlined`, else `primary`. */
  color?: 'primary' | 'neutral' | 'destructive'
  /**
   * Button treatment for the left action, named to match `Button`'s own prop. Defaults to `plain`,
   * the text-link look. Use `outlined` when the footer carries two right-aligned CTAs and the left
   * action needs equal visual weight. Independent of `color`.
   */
  variant?: 'plain' | 'outlined'
}

type SecondaryAction = Action & {
  /** Override the secondary button color. Defaults to `neutral` for `destructive` footers, else `primary`. */
  color?: 'primary' | 'neutral' | 'destructive'
}

/** Optional tertiary action or text rendered flush left (typically a plain/tertiary button). */
type TertiaryOptions =
  | {
      tertiaryAction: TertiaryAction
      tertiaryText?: never
    }
  | {
      tertiaryAction?: undefined
      tertiaryText?: React.ReactNode
    }

export type ModalFooterWithActionsProps = TertiaryOptions & {
  /**
   * `default`: outlined primary (secondary) + filled primary (primary action).
   * `destructive`: outlined neutral (secondary) + filled destructive (primary action).
   */
  type?: ModalFooterWithActionsType
  /** Optional message above the action buttons. Used for surfacing warnings and inline errors. */
  inlineMessage?: InlineMessageProps
  /** Optional secondary action rendered to the left of the primary action. */
  secondaryAction?: SecondaryAction
  /** Optional primary action rendered right-aligned. */
  primaryAction?: PrimaryAction
}

/**
 * ModalFooterWithActions provides the canonical modal footer layout: optional `InlineMessage`,
 * then a tertiary action on the left with secondary and primary actions on the right.
 */
export const ModalFooterWithActions: React.FC<ModalFooterWithActionsProps> = ({
  type = ModalFooterWithActionsType.DEFAULT,
  inlineMessage,
  tertiaryAction,
  tertiaryText,
  secondaryAction,
  primaryAction,
}) => {
  const isDestructive = type === ModalFooterWithActionsType.DESTRUCTIVE

  const actionsRow = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 w-full items-center justify-center sm:w-auto sm:justify-start sm:flex-1 [&>*]:w-full sm:[&>*]:w-auto">
        {tertiaryAction && (
          <Button
            disabled={tertiaryAction.disabled}
            leadingIcon={tertiaryAction.icon}
            color={tertiaryAction.color ?? (tertiaryAction.variant === 'outlined' ? 'neutral' : 'primary')}
            variant={tertiaryAction.variant ?? 'plain'}
            onClick={tertiaryAction.onClick}
          >
            {tertiaryAction.label}
          </Button>
        )}
        {!tertiaryAction && tertiaryText && (
          <span className="text-sm text-text-tertiary max-w-full break-all line-clamp-2" title={typeof tertiaryText === 'string' ? tertiaryText : undefined}>
            {tertiaryText}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-end w-full sm:w-auto [&>*]:w-full sm:[&>*]:w-auto">
        {secondaryAction && (
          <Button
            className="order-2 sm:order-1"
            color={secondaryAction.color ?? (isDestructive ? 'neutral' : 'primary')}
            disabled={secondaryAction.disabled}
            leadingIcon={secondaryAction.icon}
            trailingIcon={secondaryAction.trailingIcon}
            onClick={secondaryAction.onClick}
            variant="outlined"
          >
            {secondaryAction.label}
          </Button>
        )}
        {primaryAction && (
          <Button
            className="order-1 sm:order-2"
            color={isDestructive ? 'destructive' : 'primary'}
            disabled={primaryAction.disabled}
            leadingIcon={primaryAction.icon}
            trailingIcon={primaryAction.trailingIcon}
            loading={primaryAction.loading}
            onClick={primaryAction.onClick}
            variant="filled"
          >
            {primaryAction.label}
          </Button>
        )}
      </div>
    </div>
  )

  if (!inlineMessage) return actionsRow

  return (
    <div className="flex flex-col gap-4">
      <InlineMessage {...inlineMessage} />
      {actionsRow}
    </div>
  )
}
